import type { ToolRegistryEntry } from "@/types/tools";
import { db } from "@/lib/db";

/**
 * Load tool registry from IndexedDB
 */
export async function loadToolRegistry(): Promise<ToolRegistryEntry[]> {
  return await db.toolRegistry.toArray();
}

/**
 * Get enabled tools for a profile
 */
export async function getEnabledTools(
  agentModeEnabled: boolean
): Promise<ToolRegistryEntry[]> {
  if (!agentModeEnabled) {
    return [];
  }

  const allTools = await loadToolRegistry();
  return allTools.filter((tool) => tool.enabled);
}

/**
 * Initialize default tools in registry
 */
export async function initializeToolRegistry(): Promise<void> {
  const existing = await db.toolRegistry.count();
  if (existing > 0) {
    return; // Already initialized
  }

  const defaultTools: ToolRegistryEntry[] = [
    {
      name: "create_task",
      description: "Create a new task in the user's task list",
      inputSchema: {
        type: "object",
        properties: {
          title: {
            type: "string",
            description: "The task title",
          },
          description: {
            type: "string",
            description: "Optional task description",
          },
          priority: {
            type: "string",
            enum: ["low", "medium", "high"],
            description: "Task priority",
          },
        },
        required: ["title"],
      },
      enabled: true,
      scope: "local",
      implementation: "create_task",
    },
    {
      name: "draft_email",
      description: "Draft an email and save it locally",
      inputSchema: {
        type: "object",
        properties: {
          to: {
            type: "string",
            description: "Recipient email address",
          },
          subject: {
            type: "string",
            description: "Email subject",
          },
          body: {
            type: "string",
            description: "Email body text",
          },
        },
        required: ["to", "subject", "body"],
      },
      enabled: true,
      scope: "local",
      implementation: "draft_email",
    },
  ];

  await db.toolRegistry.bulkAdd(defaultTools);
}

